import { array } from '@ember/helper';
import { HStack, Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import ApproverComments from 'tio-common/components/tuition-assistance/approver-comments';
import AttachmentList from 'tio-common/components/tuition-assistance/forms/upload-attachments/attachment-list';
import Component from '@glimmer/component';
import TuitionAssistanceApplicationDetailsApproverOverview from 'tio-common/components/tuition-assistance/application-details/approver-overview';
import TuitionAssistanceApplicationDetailsCoursesStatusApproval from 'tio-common/components/tuition-assistance/application-details/courses-status-approval';
import TuitionAssistanceApplicationDetailsEmployeeInformation from 'tio-common/components/tuition-assistance/application-details/employee-information';
import TuitionAssistanceProgramDetailsAppDetailsAppHistory from 'tio-common/components/tuition-assistance/program-details/app-details/app-history';
import TuitionAssistanceProgramDetailsAppDetailsReadonlyCourseCard from 'tio-common/components/tuition-assistance/program-details/app-details/readonly-course-card';
import TuitionAssistanceProgramDetailsAppDetailsScholarshipCard from 'tio-common/components/tuition-assistance/program-details/app-details/scholarship-card';
import TuitionAssistanceProgramDetailsApplicationInformation from 'tio-common/components/tuition-assistance/program-details/application-information';
import TuitionAssistanceProgramDetailsProgramInfoCard from 'tio-common/components/tuition-assistance/program-details/program-info-card';
import type SessionContextService from 'tio-employee/services/session-context';
import type TasApplicationModel from 'tio-data/models/tas-application';

interface RouteSignature {
  Args: {
    model: TasApplicationModel;
  };
}

export default class TuitionAssistanceApplicationsShowReadonlyRouteComponent extends Component<RouteSignature> {
  @service declare sessionContext: SessionContextService;

  get allAttachments() {
    // Assets can be attached to the course or the application
    const courseAssets = this.args.model.activeCourses.flatMap((course) => course.tasAssets || []);
    const appAssets = this.args.model.tasAssets;
    return [...courseAssets, ...appAssets];
  }

  get partnerMessagingEnabled() {
    const companySetting =
      this.args.model.tasProgramInstance.tasProgramTemplate.company.companySetting;
    return companySetting?.tuitionAssistance?.isPartnerMessagingEnabled;
  }

  <template>
    <HStack>
      <VStack class="lg:w-2/3! md:w-1/2!">
        <TuitionAssistanceApplicationDetailsEmployeeInformation
          @instance={{@model.tasProgramInstance}}
          @application={{@model}}
          @profileRoute="authenticated.admin.employees.show"
          @routeModel={{@model.tasProgramInstance.employee.id}}
        />
        <TuitionAssistanceProgramDetailsApplicationInformation
          @application={{@model}}
          @isAdminView={{true}}
          @canRequestRepayment={{this.sessionContext.user.isAccountOwnerOrEmployeeAdmin}}
          @isReadonlyView={{true}}
          @requestingApp="employee"
        />
        <TuitionAssistanceProgramDetailsProgramInfoCard
          @programInstance={{@model.tasProgramInstance}}
          @showAttachments={{true}}
        />
      </VStack>
      <Section>
        <VStack @collapsed={{true}}>
          <ApproverComments
            @commentable={{@model}}
            @commentableType="TasApplication"
            @authoredByPerson={{this.sessionContext.user.person}}
            @commentTags={{array "approver"}}
          />
          {{#if this.partnerMessagingEnabled}}
            <ApproverComments
              @commentable={{@model}}
              @commentableType="TasApplication"
              @authoredByPerson={{this.sessionContext.user.person}}
              @commentTags={{array "Partner Messaging"}}
            />
          {{/if}}
          <TuitionAssistanceApplicationDetailsApproverOverview
            @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
          />
        </VStack>
      </Section>
    </HStack>
    <Section>
      <:body>
        <div class="tio-flex-center flex-wrap mb-4 gap-5 md:justify-start">
          {{#each @model.activeCourses as |course|}}
            <div>
              <TuitionAssistanceProgramDetailsAppDetailsReadonlyCourseCard
                @course={{course}}
                @showCustomFields={{true}}
              />
            </div>
          {{/each}}
          <div>
            <TuitionAssistanceProgramDetailsAppDetailsScholarshipCard
              @application={{@model}}
              @isEditable={{false}}
            />
          </div>
        </div>
      </:body>
    </Section>

    <Section>
      <:header>
        <h2 class="tio-text-lg-semibold">
          {{t "tuition_assistance.program_details.attachments"}}
        </h2>
      </:header>
      <:body>
        {{#if this.allAttachments.length}}
          <AttachmentList
            @assets={{this.allAttachments}}
            @disabled={{true}}
            @hideDiscarded={{true}}
            class="w-full"
          />
        {{else}}
          <p class="text-sm text-tio-gray-500">
            {{t "tuition_assistance.program_details.no_attachments"}}
          </p>
        {{/if}}
      </:body>
    </Section>

    <Section>
      {{! TODO: see note on index version of this template.}}
      <TuitionAssistanceApplicationDetailsCoursesStatusApproval
        @application={{@model}}
        @programInstance={{@model.tasProgramInstance}}
        @routePrefix="authenticated"
        @isReadonlyView={{true}}
      />
    </Section>

    <TuitionAssistanceProgramDetailsAppDetailsAppHistory
      @application={{@model}}
      @viewType="admin"
    />
  </template>
}
